// Metadata shared by the Pug head, SPA fallback and client head. No UI or DB writes.
// Product surfaces retain their own canonicals and do not borrow feature-page examples.
const publicSeoMetadata = {
  "/affiliate-program-agreement": {
    title: "Affiliate Program Agreement",
    description: "Gab AI affiliate eligibility, commissions, promotion standards and termination terms.",
  },
  "/ccpa": {
    title: "CCPA Privacy Notice",
    description: "Learn about your privacy rights as a California resident under the California Consumer Privacy Act (CCPA). Understand how Gab AI collects, uses, and protects your personal information.",
  },
  "/dmca": {
    title: "Copyright Policy",
    description: "Learn about our copyright policy, including how to submit DMCA takedown notices and counter-notifications. Understand your rights and responsibilities regarding copyright-protected content on Gab AI's platform.",
  },
  "/refund-policy": {
    title: "Refund Policy",
    description: "Gab AI refund policy for subscriptions, Gab AI Business seats, credit packs, and Auto Top-Up, including cancellation, billing errors, and statutory rights.",
  },
  "/terms-of-sale": {
    title: "Terms of Sale",
    description: "Gab AI Terms of Sale. Subscription billing, payment methods, refund policy, and cancellation terms for Gab AI Plus, Plus Premium, Gab AI Business seats, and credits.",
  },
  "/terms-of-service": {
    title: "Terms of Service",
    description: "Gab AI Terms of Service. User agreement, rights, responsibilities, and guidelines for using our AI platform, including Gab AI Business company workspaces. Must be 18+ to use.",
  },
  "/sites/report": {
    title: "Report a site",
    description: "Report a specific Gab AI-published site with its address, reason and relevant details. Reports are reviewed; removal is not automatic.",
  },
  "/feedback": {
    title: "Gab AI Feedback: Ideas & Product Suggestions",
    description: "Share a product suggestion or describe a reproducible problem. Use Contact for account or billing support.",
  },
  "/contact": {
    title: "Contact Gab AI Support",
    description: "Contact Gab AI support team. Get account, billing or technical help. Describe the problem and choose a reason; use Feedback for product ideas.",
  },
  "/llms": {
    title: "Gab AI Product & Source Directory",
    description: "Find official Gab AI sources for features, models, pricing, API access, documentation, product changes and privacy policies.",
  },
  "/changelog": {
    title: "What's New — Gab AI Changelog",
    description: "Read dated Gab AI product changes and find current feature, setup and usage references. App editions, installed versions, account access and model availability can differ. Latest recorded update October 9, 2026.",
  },
  "/brand": {
    title: "Brand Guidelines & Logos | Gab AI",
    description: "Preview black and white Gab AI logos and read our minimal design guidelines for typography, layout, product videos, motion, and sound.",
  },
  "/based-benchmark": {
    title: "Based Benchmark: AI Political Bias & Compass Results | Gab AI",
    description: "Inspect Gab’s dated AI political compass snapshot, protocol, prompting differences and limits. Political alignment is not accuracy or an overall model ranking.",
  },
  "/galaxy-ai-alternative": {
    title: "GalaxyAI / Magica Alternative: Tasks & Model Access | Gab AI",
    description: "Compare Gab AI with GalaxyAI’s Magica for agent work, files, models, media, API access and costs. See Windows/Mac apps, iPhone/Android Remote and supported imports.",
  },
  "/t3chat-alternative": {
    title: "t3.chat Alternative: Models, Allowances & Usage | Gab AI",
    description: "Compare Gab AI and t3.chat for models, repeated tasks, usage meters, context and costs. See Compare, editable Work, Windows/Mac apps, iPhone/Android Remote and imports.",
  },
  "/typingmind-alternative": {
    title: "TypingMind Alternative: BYOK, Models & Costs | Gab AI",
    description: "Compare Gab AI and TypingMind for BYOK access, licenses, multi-model chat, projects, storage and costs. See Work, Windows/Mac apps, iPhone/Android Remote and imports.",
  },
  "/chathub-alternative": {
    title: "ChatHub AI Alternative: Multi-Model Chat | Gab AI",
    description: "Compare Gab AI and ChatHub AI for parallel model answers, DeepShelf projects, media, keys and costs. See Work, Windows/Mac apps, iPhone/Android Remote and imports.",
  },
  "/monica-alternative": {
    title: "Monica Alternative for Browser AI Workflows | Gab AI",
    description: "Compare Gab AI and Monica for browser assistance, model comparison, research, editable Work, costs and chat imports. See Windows/Mac apps and iPhone/Android Remote.",
  },
  "/poe-alternative": {
    title: "Poe Alternative: Models, Media & Usage Costs | Gab AI",
    description: "Compare Gab AI with Poe for models, media, points, credits and API access. See Work, Windows/Mac apps, iPhone/Android Remote and supported chat imports.",
  },
  "/chatgpt-alternative": {
    title: "ChatGPT Alternative: Models, Memory & Work | Gab AI",
    description: "Compare Gab AI and ChatGPT for writing, model selection, memory, Work, costs and supported chat imports. Use Gab AI on Windows, Mac, iPhone and Android.",
  },
  "/alternatives": {
    title: "AI Chat Alternatives: Compare Models, Tools & Costs | Gab AI",
    description: "Compare Gab AI with eight named AI products for tasks, model access, API or browser workflows, costs and supported migration. Inspect sources and real Gab examples.",
  },
  "/api": {
    title: "Gab AI API: OpenAI-Compatible Requests, Routing & Integrations",
    description: "Build with multiple AI models through one API key. Use OpenAI-compatible Chat Completions and Responses, Anthropic-style Messages, streaming and tools.",
  },
  "/christian-ai": {
    title: "Christian AI Chatbot for Bible Study | Gab AI",
    description: "Christian AI chat with Arya for Bible study, questions and writing. Explore Scripture, prepare a devotional and adapt discussion questions for your family or church.",
  },
  "/about": {
    title: "About Gab AI: Product, Company & Common Questions",
    description: "Learn what Gab AI does, who operates the service, and how Arya, provider models, creative tools and Work fit together.",
  },
  "/extension": {
    title: "Gab AI Browser Extension: Side Chat & Page Questions",
    description: "Add Gab AI to Chrome or Brave. Summarize readable pages, ask about selected text and draft replies in a sidebar beside your work.",
  },
  "/mac": {
    title: "Gab AI for Mac: AI Assistant, Files & Local Work",
    description: "Use Gab AI on your Mac to write, research, ask questions about files and work with project folders. Download the direct Mac app and get started.",
  },
  "/download": {
    title: "Download Gab AI: Mac, Windows, iPhone & Android",
    description: "Download Gab AI for Mac, Windows, iPhone, iPad and Android. Chat, write, research and create with your Gab AI account, or use it on the web.",
  },
  "/": {
    title: "Gab AI — AI Chat, Work Agent & Creative Tools",
    description: "Chat with leading AI models and create websites, documents, code, videos, and games. Try Gab AI on the web, Windows, Mac, iOS, Android, and in your browser.",
  },
  "/features/explore": {
    title: "Explore AI Examples, Prompts & Workflows | Gab AI",
    description: "Inspect AI examples and choose a relevant workflow. Understand inputs, revisions and limits before adapting an example for your task.",
  },
  "/features/prompts": {
    title: "AI Prompt Templates: Inputs, Examples & Revisions | Gab AI",
    description: "Use a prompt template as a starting point. Replace the inputs, request a clear output, check the result and revise one requirement at a time.",
  },
  "/features/memory": {
    title: "AI Memory: Remember, Review & Edit Context | Gab AI",
    description: "Save useful context between conversations, review or edit your memories, and understand how memory differs from chat history and custom instructions.",
  },
  "/features/tools": {
    title: "AI Tools: Guided Inputs, Results & Revisions | Gab AI",
    description: "Choose a guided AI tool for a specific result, provide the required inputs and inspect the model, credit estimate and output before revising.",
  },
  "/features/models": {
    title: "Compare AI Models: Inputs, Tools, Access & Results | Gab AI",
    description: "Use GPT, Claude, Gemini, DeepSeek and more AI models in one Gab AI account. Switch models in the same chat for writing, research, files and coding.",
  },
  "/features/agents": {
    title: "Custom AI Bots: Instructions, Examples & Access | Gab AI",
    description: "Give a Gab AI bot a clear role and reusable instructions. Try a focused request, revise it and check model access before returning to the bot.",
  },
  "/features/tasks": {
    title: "Scheduled AI Tasks: Timing, Results & Credits | Gab AI",
    description: "Configure a scheduled AI task with clear inputs, timing and delivery. Understand Chat versus Work, review results and check usage before repeating.",
  },
  "/features/music": {
    title: "AI Music Generator: Songs & Instrumentals | Gab AI",
    description: "Create music from a prompt or supply original lyrics in Gab AI. Listen to real samples, compare a prompt revision and check the model and credit estimate.",
  },
  "/features/videos": {
    title: "AI Video Generator: Text & Image to Video | Gab AI",
    description: "Explore text-to-video and image-to-video workflows in Gab AI. Learn to describe motion, choose available controls and review credits before starting a clip.",
  },
  "/features/images": {
    title: "AI Image Generator & Image Editing | Gab AI",
    description: "Create images from text and edit photos with Gab AI. Explore a real prompt-and-edit example, choose an image model and start your own image.",
  },
  "/features/files": {
    title: "AI PDF & File Analysis: Summaries and Answers | Gab AI",
    description: "Chat with PDFs and documents in Gab AI. Summarize reports, ask questions and extract tables, with source details you can check.",
  },
  "/features/presentations": {
    title: "AI Presentation Maker: Editable Slides & PowerPoint | Gab AI",
    description: "Turn your notes into editable AI presentations and PowerPoint slides. Add tables and speaker notes, then refine your deck with Gab Work.",
  },
  "/features/documents": {
    title: "AI Document Generator: Editable Word Documents | Gab AI",
    description: "Create editable Word documents with Gab AI. Turn your notes into proposals and briefs, build comparison tables and refine your draft with Work.",
  },
  "/features/spreadsheets": {
    title: "AI Spreadsheet Generator: Editable Excel Formulas | Gab AI",
    description: "Create editable Excel spreadsheets with Gab AI. Build budgets and tables with formulas, compare scenarios and refine your workbook in Gab Work.",
  },
  "/features": {
    title: "Gab AI Features — Every Kind of AI in One Workspace",
    description: "Explore Gab AI chat, Work, images, video, music, voice, files, memory and API access. Inspect examples and compare model, plan and credit requirements.",
  },
  "/free-ai-chat": {
    title: "Free AI Chat & AI Chatbot | Gab AI",
    description: "Start free AI chat with Arya and selected models. Write, ask questions, summarize notes and get code help, then refine the answer in the same conversation.",
  },
  "/switch": {
    title: "Import AI Chat History into Gab AI: Formats & Limits",
    description: "Bring supported ChatGPT, Claude, Grok or Gab AI history into Gab. Check text, dates, omissions, duplicates and separate memory setup before continuing.",
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description: "How Gab AI handles conversations, Work files and browser sessions, connected services, payment information, and your privacy choices.",
  },
  "/openrouter-alternative": {
    title: "OpenRouter Alternative: API Routing, Models & Costs | Gab AI",
    description: "Compare Gab AI and OpenRouter for API routing, models, keys, fees, Chat and agent workflows. See editable Work, Windows/Mac apps, iPhone/Android Remote and imports.",
  },
  "/right-wing-ai": {
    title: "Conservative AI, Christian AI & Right-Wing AI — Arya by Gab AI",
    description: "Chat with Arya, Gab’s conservative, Christian and right-wing AI. Explore politics and faith, study Scripture, develop arguments and write in your own voice.",
  },
  "/voice": {
    title: "Talk to AI — Real-Time AI Voice Chat | Gab AI",
    description: "Talk to AI in Gab AI Voice Mode, ask follow-ups and return to the saved chat. Watch the recorded walkthrough and check Plus access and credits.",
  },
  '/images': {
    title: 'Free AI Image Generator — Create Images from Text | Gab AI',
    description: 'Create AI images from text with eligible models and finite signup credits. Sign in, check model access and review the credit estimate before generating.',
  },
  '/models': {
    title: 'AI Models: Available Inputs, Tools & Access | Gab AI',
    description: 'Browse available AI models and check supported inputs, tools and credit costs. Model availability and access depend on your account and selected feature.',
  },
};

function getPublicSeoMetadata(canonical) {
  if (typeof canonical !== 'string') return null;
  let url;
  try { url = new URL(canonical, 'https://gab.ai'); } catch { return null; }
  if (url.origin !== 'https://gab.ai') return null;
  return publicSeoMetadata[url.pathname.replace(/\/+$/, '') || '/'] || null;
}

function pricingSeoMetadata(monthly, annual) {
  return {
    title: 'Gab AI Pricing: Plans, Credits & Usage Costs',
    description: `Compare Free, Plus and Premium access and credits. Plus is ${monthly} monthly or ${annual} annually; review model, platform and billing conditions.`,
  };
}

// Align the page node only. Keep source FAQs, breadcrumbs and stored articles
// intact; page metadata is not authorization to rewrite their content.
function alignPublicPageSchema(canonical, value) {
  const metadata = getPublicSeoMetadata(canonical);
  if (!metadata || !value) return value;
  if (Array.isArray(value)) return value.map(node => alignPublicPageSchema(canonical, node));
  if (value['@graph']) return { ...value, '@graph': alignPublicPageSchema(canonical, value['@graph']) };
  if ([].concat(value['@type'] || []).includes('WebPage') &&
      (!value.url || getPublicSeoMetadata(value.url) === metadata)) {
    return { ...value, name: metadata.title, description: metadata.description };
  }
  return value;
}

module.exports = { publicSeoMetadata, getPublicSeoMetadata, pricingSeoMetadata, alignPublicPageSchema };
